'use client';

import React from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Button } from '@/shared/components/ui/button';
import { Textarea } from '@/shared/components/ui/textarea';
import { Label } from '@/shared/components/ui/label';
import { cn } from '@/shared/utils/cn';
import { PhotoConsentCheckbox } from './PhotoConsentCheckbox';
import {
  FileText,
  Upload,
  Loader2,
  Pencil,
  Sparkles,
} from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export type GenerationSource = 'description' | 'photo';

interface CharacterPortraitControlsProps {
  portraitSource: GenerationSource;
  onPortraitSourceChange: (source: GenerationSource) => void;
  uploadedPhoto: File | null;
  onUploadedPhotoChange: (file: File | null) => void;
  /** Guardian-consent gate (Art. 9) for using a photo. */
  photoConsent: boolean;
  onPhotoConsentChange: (v: boolean) => void;
  customPrompt: string;
  onCustomPromptChange: (prompt: string) => void;
  /** Character description auto-built from form fields */
  characterDescription: string;
  /** null = create mode (no inline Generate button), string = edit mode */
  characterId: string | null;
  onGenerateClick: () => void;
  isGenerating: boolean;
  portraitCost: number;
  /** Photo analysis */
  onAnalyzePhoto?: () => void;
  isAnalyzingPhoto?: boolean;
  photoAnalysisCost?: number;
  disabled?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export function CharacterPortraitControls({
  portraitSource,
  onPortraitSourceChange,
  uploadedPhoto,
  onUploadedPhotoChange,
  photoConsent,
  onPhotoConsentChange,
  customPrompt,
  onCustomPromptChange,
  characterDescription,
  characterId,
  onGenerateClick,
  isGenerating,
  portraitCost,
  onAnalyzePhoto,
  isAnalyzingPhoto,
  photoAnalysisCost = 5,
  disabled = false,
}: CharacterPortraitControlsProps) {
  const t = useTranslations('stories.characters.images');
  const tForm = useTranslations('stories.characters.form');
  const tPortraits = useTranslations('stories.characters.portraits');
  const [isEditingDescription, setIsEditingDescription] = React.useState(false);

  const effectiveDescription = customPrompt || characterDescription;

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onUploadedPhotoChange(file);
    }
  };

  const canGenerate = portraitSource === 'description'
    ? effectiveDescription.length > 0
    : uploadedPhoto !== null && photoConsent;

  return (
    <div data-testid="portrait-controls" className="space-y-4 rounded-lg border bg-card p-4">
      {/* Source Toggle */}
      <div className="space-y-2">
        <Label>{t('source.label')}</Label>
        <div className="grid grid-cols-2 gap-2">
          <button
            data-testid="portrait-source-description-button"
            type="button"
            onClick={() => onPortraitSourceChange('description')}
            className={cn(
              'flex flex-col items-center gap-1 rounded-lg border-2 p-3 transition-all',
              portraitSource === 'description'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            )}
          >
            <FileText className="h-5 w-5" />
            <span className="text-sm font-medium">{t('source.description')}</span>
          </button>
          <button
            data-testid="portrait-source-photo-button"
            type="button"
            onClick={() => onPortraitSourceChange('photo')}
            className={cn(
              'flex flex-col items-center gap-1 rounded-lg border-2 p-3 transition-all',
              portraitSource === 'photo'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            )}
          >
            <Upload className="h-5 w-5" />
            <span className="text-sm font-medium">{t('source.photo')}</span>
          </button>
        </div>
      </div>

      {/* Source Content */}
      {portraitSource === 'description' ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>{t('descriptionLabel')}</Label>
            <Button
              data-testid="portrait-edit-description-button"
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setIsEditingDescription(!isEditingDescription)}
              className="h-auto py-1 px-2 text-xs"
            >
              <Pencil className="mr-1 h-3 w-3" />
              {isEditingDescription ? t('done') : t('edit')}
            </Button>
          </div>
          {isEditingDescription ? (
            <Textarea
              data-testid="portrait-custom-description-input"
              value={customPrompt || characterDescription}
              onChange={(e) => onCustomPromptChange(e.target.value)}
              rows={3}
              placeholder={t('descriptionPlaceholder')}
            />
          ) : (
            <div className="rounded-md bg-muted p-3 text-sm text-muted-foreground">
              {effectiveDescription || t('noDescription')}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <Label>{t('photoLabel')}</Label>
          {/* Guardian-consent gate (Art. 9) — shared control + wording. */}
          <PhotoConsentCheckbox checked={photoConsent} onChange={onPhotoConsentChange} />
          {uploadedPhoto ? (
            <div className="flex items-center gap-3 rounded-md border p-3">
              <div className="relative h-16 w-16 overflow-hidden rounded-md">
                <Image
                  src={URL.createObjectURL(uploadedPhoto)}
                  alt="Preview"
                  fill
                  sizes="(max-width: 768px) 100vw, 400px"
                  className="object-cover"
                />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium">{uploadedPhoto.name}</p>
                <p className="text-xs text-muted-foreground">
                  {(uploadedPhoto.size / 1024 / 1024).toFixed(1)} MB
                </p>
              </div>
              <Button
                data-testid="portrait-remove-photo-button"
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onUploadedPhotoChange(null)}
              >
                {t('remove')}
              </Button>
            </div>
          ) : (
            <label className={cn(
              'flex flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 transition-colors',
              photoConsent ? 'cursor-pointer hover:border-primary/50 hover:bg-muted/50' : 'cursor-not-allowed opacity-50',
            )}>
              <Upload className="h-8 w-8 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                {t('uploadHint')}
              </span>
              <span className="text-xs text-muted-foreground">
                JPG, PNG {t('upTo')} 5MB
              </span>
              <input
                type="file"
                accept="image/jpeg,image/png"
                onChange={handlePhotoUpload}
                disabled={!photoConsent}
                className="hidden"
              />
            </label>
          )}
          {/* Analyze photo button */}
          {uploadedPhoto && onAnalyzePhoto && (
            <Button
              data-testid="portrait-analyze-photo-button"
              type="button"
              variant="outline"
              size="sm"
              onClick={onAnalyzePhoto}
              disabled={isAnalyzingPhoto}
              className="w-full"
            >
              {isAnalyzingPhoto ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {tForm('analyzingPhoto')}
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  {tForm('analyzePhoto')} ({photoAnalysisCost} tokens)
                </>
              )}
            </Button>
          )}
          <p className="text-xs text-muted-foreground">
            {t('photoHint')}
          </p>
        </div>
      )}

      {/* No style choice: the library avatar is a NEUTRAL master reference
          («болванка») — book generation redraws it in the book's own style. */}

      {/* Generate Button — only in edit mode */}
      {characterId && (
        <div className="flex justify-end pt-2">
          <Button
            data-testid="portrait-generate-button"
            type="button"
            onClick={onGenerateClick}
            disabled={!canGenerate || isGenerating}
          >
            {isGenerating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('generating')}
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                {tPortraits('generatePortrait')} ({portraitCost} tokens)
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
}
